import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ArrowLeft, 
  Plus,
  Repeat,
  Pause,
  Play,
  Trash2,
  Calendar,
  Wallet,
  User,
  Receipt,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const RecurringPayments = ({ user }) => {
  const navigate = useNavigate();
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [creating, setCreating] = useState(false);
  
  const [newPayment, setNewPayment] = useState({
    name: "",
    amount: "",
    currency: "JMD",
    frequency: "monthly",
    wallet_id: "",
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("stelx_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [recurringRes, walletsRes] = await Promise.all([
        axios.get(`${API}/recurring/list`, { headers }),
        axios.get(`${API}/wallets`, { headers })
      ]);
      
      setRecurring(recurringRes.data.recurring_payments || []);
      setWallets(walletsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load recurring payments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPayment.name || !newPayment.amount || !newPayment.wallet_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("stelx_token");
      await axios.post(
        `${API}/recurring/create`,
        {
          ...newPayment,
          amount: parseFloat(newPayment.amount),
          start_date: new Date(newPayment.start_date).toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Recurring payment created!");
      setShowCreate(false);
      setNewPayment({
        name: "",
        amount: "",
        currency: "JMD",
        frequency: "monthly",
        wallet_id: "",
        start_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      console.error("Error creating recurring payment:", error);
      toast.error("Failed to create recurring payment");
    } finally {
      setCreating(false);
    }
  };

  const handlePause = async (recurringId) => {
    try {
      const token = localStorage.getItem("stelx_token");
      await axios.put(
        `${API}/recurring/${recurringId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment paused");
      fetchData();
    } catch (error) {
      toast.error("Failed to pause payment");
    }
  };

  const handleResume = async (recurringId) => {
    try {
      const token = localStorage.getItem("stelx_token");
      await axios.put(
        `${API}/recurring/${recurringId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment resumed");
      fetchData();
    } catch (error) {
      toast.error("Failed to resume payment");
    }
  };

  const handleCancel = async (recurringId) => {
    if (!confirm("Are you sure you want to cancel this recurring payment?")) return;
    
    try {
      const token = localStorage.getItem("stelx_token");
      await axios.delete(
        `${API}/recurring/${recurringId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel payment");
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      yearly: "Yearly"
    };
    return labels[freq] || freq;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-emerald-500/20 text-emerald-400",
      paused: "bg-amber-500/20 text-amber-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-white/10 text-white";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/5 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-heading font-semibold text-xl">Recurring Payments</h1>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            size="sm"
            className="btn-primary h-9"
          >
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-4">
        {recurring.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Repeat className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">No Recurring Payments</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Set up automatic payments for bills, transfers, or savings.
            </p>
            <Button onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create First Payment
            </Button>
          </div>
        ) : (
          recurring.map((payment) => (
            <div key={payment.recurring_id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Repeat className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{payment.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {getFrequencyLabel(payment.frequency)}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                  {payment.status}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-t border-white/5">
                <div>
                  <p className="text-lg font-bold text-white">
                    {payment.currency} {payment.amount.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Next: {new Date(payment.next_payment_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {payment.status === "active" ? (
                    <button
                      onClick={() => handlePause(payment.recurring_id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Pause"
                    >
                      <Pause className="w-4 h-4 text-amber-400" />
                    </button>
                  ) : payment.status === "paused" ? (
                    <button
                      onClick={() => handleResume(payment.recurring_id)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Resume"
                    >
                      <Play className="w-4 h-4 text-emerald-400" />
                    </button>
                  ) : null}
                  {payment.status !== "cancelled" && (
                    <button
                      onClick={() => handleCancel(payment.recurring_id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Cancel"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  )}
                </div>
              </div>

              <div className="text-xs text-muted-foreground mt-2">
                {payment.total_payments_made} payments made
              </div>
            </div>
          ))
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="glass-heavy border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">New Recurring Payment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-muted-foreground text-sm">Payment Name</Label>
              <Input
                placeholder="e.g., Rent, Netflix, Savings"
                className="input-glass mt-2"
                value={newPayment.name}
                onChange={(e) => setNewPayment({ ...newPayment, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground text-sm">Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  className="input-glass mt-2"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Currency</Label>
                <select
                  className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                  value={newPayment.currency}
                  onChange={(e) => setNewPayment({ ...newPayment, currency: e.target.value })}
                >
                  <option value="JMD">JMD</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Frequency</Label>
              <select
                className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                value={newPayment.frequency}
                onChange={(e) => setNewPayment({ ...newPayment, frequency: e.target.value })}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">From Wallet</Label>
              <select
                className="w-full h-10 mt-2 px-3 rounded-lg input-glass bg-white/[0.04] border border-white/[0.08]"
                value={newPayment.wallet_id}
                onChange={(e) => setNewPayment({ ...newPayment, wallet_id: e.target.value })}
              >
                <option value="">Select wallet</option>
                {wallets.map((wallet) => (
                  <option key={wallet.wallet_id} value={wallet.wallet_id}>
                    {wallet.currency} - {wallet.available_balance.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Start Date</Label>
              <Input
                type="date"
                className="input-glass mt-2"
                value={newPayment.start_date}
                onChange={(e) => setNewPayment({ ...newPayment, start_date: e.target.value })}
              />
            </div>

            <Button
              onClick={handleCreate}
              disabled={creating}
              className="w-full btn-primary h-11 mt-4"
            >
              {creating ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "Create Recurring Payment"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringPayments;
